import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  EmployeeRegularHoursParams,
  HookParams,
  RegularWorkingHour,
  WeekDay,
} from '@/libs/interfaces'
import { employeesApi } from '@/services/api'

const ALL_WEEK_DAYS: WeekDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

// The backend returns startTime/endTime as "HH:MM:SS" and rest as a
// millisecond duration (see EmployeeRegularHour) — both get normalized to
// "HH:MM" for the <Input type="time"> table.
const toHHMM = (time: string): string => time.slice(0, 5)

const msToHHMM = (durationMs: number): string => {
  const totalMinutes = Math.round(durationMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// The backend's create/update body requires a full "HH:MM:SS" duration
// string for startTime/endTime/rest (iso-time format) — an empty value
// falls back to midnight/zero rather than being sent at all, since callers
// only submit days that have both start and end times set.
const hhmmToDurationString = (value: string): string => (value ? `${value}:00` : '00:00:00')

export function useEmployeeRegularHours({
  params,
  enabled,
}: HookParams<EmployeeRegularHoursParams>) {
  return useQuery<RegularWorkingHour[]>({
    queryKey: QUERY_KEYS.employeeWorkingHours.id(params.employeeID, params.storeID),
    queryFn: async () => {
      const hours = await employeesApi.getRegularHours(params)
      return ALL_WEEK_DAYS.map((weekDay) => {
        const existing = hours.find((hour) => hour.weekDay === weekDay)
        return existing
          ? {
              weekDay,
              startTime: toHHMM(existing.startTime),
              endTime: toHHMM(existing.endTime),
              rest: msToHHMM(existing.rest),
            }
          : { weekDay, startTime: '', endTime: '', rest: '' }
      })
    },
    enabled: (enabled ?? true) && !!params.employeeID && !!params.storeID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export interface SaveRegularWorkingHoursPayload extends EmployeeRegularHoursParams {
  /** Weekdays that already had a saved row as of the last fetch — determines create vs. update vs. delete per day. */
  existingWeekDays: WeekDay[]
  hours: RegularWorkingHour[]
}

const isDaySet = (hour: RegularWorkingHour) => !!hour.startTime && !!hour.endTime

const toRegularHourInput = (hour: RegularWorkingHour) => ({
  weekDay: hour.weekDay,
  startTime: hhmmToDurationString(hour.startTime),
  endTime: hhmmToDurationString(hour.endTime),
  rest: hhmmToDurationString(hour.rest),
})

/** Thrown by useEmployeeRegularHoursSave when at least one create/update/delete
 * batch succeeded and at least one failed, so callers can show a "some days
 * saved, some didn't" message instead of a generic failure. */
class WorkingHoursPartialSaveError extends Error {
  constructor() {
    super('working-hours-partial-save-failure')
    this.name = 'WorkingHoursPartialSaveError'
  }
}

export function useEmployeeRegularHoursSave() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: async ({
      employeeID,
      storeID,
      existingWeekDays,
      hours,
    }: SaveRegularWorkingHoursPayload) => {
      const setDays = hours.filter(isDaySet)
      const toCreate = setDays.filter((hour) => !existingWeekDays.includes(hour.weekDay))
      const toUpdate = setDays.filter((hour) => existingWeekDays.includes(hour.weekDay))
      const toDelete = hours
        .filter((hour) => !isDaySet(hour) && existingWeekDays.includes(hour.weekDay))
        .map((hour) => hour.weekDay)

      const requests = [
        toCreate.length
          ? employeesApi.createRegularHours({
              employeeID,
              storeID,
              hours: toCreate.map(toRegularHourInput),
            })
          : undefined,
        toUpdate.length
          ? employeesApi.updateRegularHours({
              employeeID,
              storeID,
              weekDay: toUpdate[0].weekDay,
              hours: toUpdate.map(toRegularHourInput),
            })
          : undefined,
        toDelete.length
          ? employeesApi.deleteRegularHours({ employeeID, storeID, weekDays: toDelete })
          : undefined,
      ].filter((request): request is Promise<void> => request !== undefined)

      // Promise.allSettled (not Promise.all) so one failing batch doesn't
      // prevent the others from completing — otherwise a save that partially
      // succeeds looks identical to a save that fully failed, and the next
      // attempt recomputes existingWeekDays from the stale pre-save cache,
      // resending an already-created day as another create (which the
      // backend's unique constraint rejects indefinitely).
      const results = await Promise.allSettled(requests)
      const failures = results.filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      )
      const succeededCount = results.length - failures.length

      // Invalidate whenever at least one batch succeeded — regardless of
      // overall outcome — so the next save attempt reads existingWeekDays
      // from fresh post-save server state rather than the pre-save cache.
      if (succeededCount > 0) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.employeeWorkingHours.id(employeeID, storeID),
        })
      }

      if (failures.length === 0) return

      failures.forEach((failure) =>
        console.error('Failed to save employee working hours batch:', failure.reason),
      )

      if (succeededCount > 0) throw new WorkingHoursPartialSaveError()
      throw new Error('working-hours-save-failure')
    },
    onError: (error) => {
      toast.error(
        getErrorToastMessage(
          error,
          t(
            error instanceof WorkingHoursPartialSaveError
              ? 'account.workingHoursSavePartialFailedToast'
              : 'account.workingHoursSaveFailedToast',
          ),
        ),
      )
    },
  })
}
